/* =======================================================================
   assets/quiz-petgently-scooting.js

   PetGently Itch Pattern Test runtime - short form (v2).

   CHANGES FROM V1
   ---------------
   - Result-type routing removed. resolveResultType / severity scoring /
     buildResultUrl param spray are gone. Completion redirects to one
     offer URL, clean, with optional UTM pass-through.
   - Landing screen removed. Block 0 carries the hero intro and is a
     live question; the sticky header appears from block 1 onward.
   - Breed dropdown and 1-5 slider bindings removed.
   - Dead call to selectInterstitialVariant() removed (never defined).

   Answers still ride to the order as cart attributes and to the Apps
   Script endpoint on every step. The endpoint payload keeps its original
   key set so the existing Sheet columns do not shift; keys belonging to
   removed questions are simply sent empty.
   ======================================================================= */

(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-quiz-section]');
    if (!root) return;

    var debug = root.dataset.debug === 'true';
    var log = debug
      ? function () { console.log.apply(console, ['[PetGentlyItchQuiz]'].concat([].slice.call(arguments))); }
      : function () {};

    // ----- DOM refs -----
    var blocks = Array.prototype.slice.call(root.querySelectorAll('.quiz-pgsc-block'));
    if (blocks.length === 0) { log('No blocks found.'); return; }

    var TRACKABLE_STEP_TYPES = ['text-input', 'quiz-question', 'dual-select', 'interstitial'];
    var REAL_STEP_TYPES = ['text-input', 'quiz-question', 'dual-select'];
    var EXCLUSIVE_VALUES = ['no_signs', 'none', 'secondary_none'];

    var headerEl = root.querySelector('[data-quiz-header]');
    var backBtn = root.querySelector('[data-quiz-back]');
    var stepLabel = root.querySelector('[data-quiz-step]');
    var progressFill = root.querySelector('[data-quiz-progress]');

    // ----- Config (data attributes) -----
    var offerUrl = root.dataset.offerUrl || '/';
    var forwardUtm = root.dataset.forwardUtm === 'true';
    var autoAdvanceRadio = root.dataset.autoAdvanceRadio !== 'false';
    var storageKey = root.dataset.storageKey || 'quiz:petgently-scooting';

    // ----- State -----
    var sessionId = generateSessionId();
    var startedAt = Date.now();
    var history = [];
    var currentIdx = -1;
    var hasStarted = false;
    var answers = {};

    var personalization = {
      dog_name: '',
      pronoun: 'he',
      sex: '',
      neuter: '',
      age: '',
      weight_bracket: '',
      q5_symptoms_top_2: '',
      q6_pattern: '',
      q7_count: 'zero',
      q7_count_numeric: 0,
      q7_callout_list: '',
      q10_medication: '',
      value_anchor: '',
      previous_brand: '',
      tried_category: '',
      drug_interaction_flag: false,
      copy_test_name: '',
      copy_variant: 'a'
    };

    log('Initialized', { blockCount: blocks.length, offerUrl: offerUrl });

    var copyTest = initializeCopyTest();
    rehydrate();
    personalization.copy_test_name = copyTest.name;
    personalization.copy_variant = copyTest.variant;
    bindAllBlocks();

    if (currentIdx === -1) {
      goToBlock(0, true);
    } else if (blocks[currentIdx]) {
      // Restored mid-quiz: re-fire block-shown so timed blocks (interstitial,
      // loading) resume instead of stalling after a refresh.
      blocks[currentIdx].dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: currentIdx } }));
    }

    updateChrome();
    pushQuizEvent('quiz_viewed');

    /* ======== Block binding ======== */

    function bindAllBlocks() {
      blocks.forEach(function (block, idx) {
        switch (block.dataset.blockType) {
          case 'quiz-question':
            bindQuizQuestion(block, idx);
            break;
          case 'text-input':
            bindTextInput(block, idx);
            break;
          case 'dual-select':
            bindDualSelect(block, idx);
            break;
          case 'interstitial':
            bindInterstitial(block, idx);
            break;
          case 'affirmation':
            bindAffirmation(block, idx);
            break;
          case 'loading':
            bindLoading(block, idx);
            break;
        }
      });

      if (backBtn) {
        backBtn.addEventListener('click', goBack);
      }
    }

    function bindQuizQuestion(block, idx) {
      var labels = Array.prototype.slice.call(block.querySelectorAll('.qpgsc-arow'));
      var inputs = Array.prototype.slice.call(block.querySelectorAll('.qpgsc-answer__input'));
      var inputType = inputs.length > 0 ? inputs[0].type : 'radio';
      var nextBtn = block.querySelector('.qpgsc-next');

      labels.forEach(function (label) {
        var input = label.querySelector('.qpgsc-answer__input');
        if (!input) return;

        // Selection is driven from a direct click on the row. Hidden inputs inside
        // a <label> don't reliably fire 'change' on every mobile browser when the
        // tap lands on a child span. preventDefault stops the native label->input
        // toggle from double firing and cancelling our own change.
        label.addEventListener('click', function (e) {
          e.preventDefault();
          if (inputType === 'radio') {
            inputs.forEach(function (inp) { inp.checked = false; });
            input.checked = true;
          } else {
            input.checked = !input.checked;
          }
          handleQuestionAnswer(block, idx, label, input, inputType, nextBtn);
        });
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          finalizeQuestionAnswer(block, idx, inputType);
          advanceFrom(idx);
        });
      }
    }

    function handleQuestionAnswer(block, idx, label, input, inputType, nextBtn) {
      label.classList.add('qpgsc-press--pressed');
      setTimeout(function () { label.classList.remove('qpgsc-press--pressed'); }, 240);

      if (inputType === 'radio') {
        Array.prototype.forEach.call(block.querySelectorAll('.qpgsc-arow'), function (l) {
          l.classList.remove('qpgsc-arow--selected');
        });
        label.classList.add('qpgsc-arow--selected');

        // The Continue button is present on every question. It unlocks on
        // selection whether or not the slide also auto-advances.
        if (nextBtn) nextBtn.disabled = false;

        if (!autoAdvanceRadio) {
          // Record the answer now so Back and refresh keep it.
          finalizeQuestionAnswer(block, idx, inputType);
          return;
        }

        setTimeout(function () {
          finalizeQuestionAnswer(block, idx, inputType);
          advanceFrom(idx);
        }, parseInt(getCssVar('--auto-advance-delay'), 10) || 360);
        return;
      }

      // Multi-select: "nothing" style answers are mutually exclusive with the rest.
      var currentValue = label.dataset.answerValue;
      if (input.checked && EXCLUSIVE_VALUES.indexOf(currentValue) !== -1) {
        Array.prototype.forEach.call(block.querySelectorAll('.qpgsc-answer__input'), function (other) {
          if (other !== input) other.checked = false;
        });
      } else if (input.checked) {
        Array.prototype.forEach.call(block.querySelectorAll('.qpgsc-arow'), function (otherLabel) {
          if (EXCLUSIVE_VALUES.indexOf(otherLabel.dataset.answerValue) !== -1) {
            var otherInput = otherLabel.querySelector('.qpgsc-answer__input');
            if (otherInput) otherInput.checked = false;
          }
        });
      }

      Array.prototype.forEach.call(block.querySelectorAll('.qpgsc-arow'), function (row) {
        var rowInput = row.querySelector('.qpgsc-answer__input');
        row.classList.toggle('qpgsc-arow--selected', !!(rowInput && rowInput.checked));
      });

      if (nextBtn) {
        var minSelections = parseInt(nextBtn.dataset.minSelections, 10) || 1;
        var checkedCount = block.querySelectorAll('.qpgsc-answer__input:checked').length;
        nextBtn.disabled = checkedCount < minSelections;
      }
    }

    function finalizeQuestionAnswer(block, idx, inputType) {
      var checkedInputs = Array.prototype.slice.call(block.querySelectorAll('.qpgsc-answer__input:checked'));
      var selections = checkedInputs.map(function (inp) {
        var label = inp.closest('.qpgsc-arow');
        var labelEl = label.querySelector('.qpgsc-arow__label');
        return {
          value: label.dataset.answerValue,
          text: labelEl && labelEl.textContent ? labelEl.textContent.trim() : label.dataset.answerText,
          route_flag: label.dataset.answerRouteFlag,
          previous_brand: label.dataset.answerPreviousBrand || null
        };
      });

      answers[idx] = {
        type: 'quiz-question',
        block_id: block.dataset.blockId,
        selections: selections,
        is_q5: block.dataset.isQ5 === 'true',
        is_q6: block.dataset.isQ6 === 'true',
        is_q7: block.dataset.isQ7 === 'true',
        is_q10: block.dataset.isQ10 === 'true'
      };

      updatePersonalizationFromQuestion(block, selections);
      markStarted();
      persist();
      log('Question answered', { idx: idx, selections: selections });
    }

    function updatePersonalizationFromQuestion(block, selections) {
      var pkey = block.dataset.personalizationKey;
      if (pkey && selections.length > 0) {
        personalization[pkey] = selections[0].value;
        personalization[pkey + '_label'] = selections[0].text;
      }

      // Symptoms: keep the first two ticked, in the order shown.
      if (block.dataset.isQ5 === 'true') {
        personalization.q5_symptoms_top_2 = selections
          .slice(0, 2)
          .map(function (s) { return s.text; })
          .join(' / ');
      }

      if (block.dataset.isQ6 === 'true' && selections.length > 0) {
        personalization.q6_pattern = selections[0].value;
        personalization.q6_pattern_label = selections[0].text;
      }

      // Already-tried: count only the flagged options, keep the prospect's wording.
      if (block.dataset.isQ7 === 'true') {
        var tried = selections.filter(function (selection) {
          return selection.route_flag === 'tried_failed';
        });
        var triedLabels = tried.map(function (selection) {
          return selection.previous_brand || selection.text;
        });
        personalization.q7_count_numeric = tried.length;
        personalization.q7_count = numberWord(tried.length);
        personalization.q7_callout_list = naturalJoinCapped(triedLabels, 3);
        personalization.tried_category = tried.length >= 2 ? 'everything_tried' : (tried.length === 1 ? 'tried_one' : 'new');
        personalization.previous_brand = triedLabels[0] || '';
      }

      // Vet medication: sets the interaction flag used for segmentation.
      if (block.dataset.isQ10 === 'true') {
        var q10Values = selections.map(function (selection) { return selection.value; });
        personalization.drug_interaction_flag = q10Values.some(function (value) {
          return ['apoquel', 'cytopoint', 'steroids', 'infection_treatment', 'prescription_diet'].indexOf(value) !== -1;
        });
        var medicationOrder = [
          { value: 'apoquel', label: 'Apoquel' },
          { value: 'cytopoint', label: 'Cytopoint' },
          { value: 'steroids', label: 'steroids' }
        ];
        personalization.q10_medication = medicationOrder
          .filter(function (item) { return q10Values.indexOf(item.value) !== -1; })
          .slice(0, 2)
          .map(function (item) { return item.label; })
          .join(' and ');
      }
    }

    function naturalJoin(items) {
      if (!items.length) return '';
      if (items.length === 1) return items[0];
      if (items.length === 2) return items[0] + ' and ' + items[1];
      return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
    }

    function naturalJoinCapped(items, cap) {
      if (!items.length) return '';
      var visible = items.slice(0, cap);
      if (items.length > cap) {
        if (visible.length === 1) return visible[0] + ' and more';
        return visible.slice(0, -1).join(', ') + ', ' + visible[visible.length - 1] + ' and more';
      }
      return naturalJoin(visible);
    }

    function numberWord(value) {
      var words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
      return words[value] || String(value);
    }

    /* ======== Text input (dog name) ======== */

    function bindTextInput(block, idx) {
      var input = block.querySelector('[data-text-input]');
      var nextBtn = block.querySelector('.qpgsc-next');
      if (!input) return;

      input.addEventListener('input', function () {
        if (nextBtn) nextBtn.disabled = input.value.trim().length === 0;
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && input.value.trim().length > 0) {
          e.preventDefault();
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        }
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (input.value.trim().length === 0) return;
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        });
      }
    }

    function finalizeTextInput(block, idx, input) {
      var pkey = input.dataset.personalizationTarget || 'dog_name';
      var val = input.value.trim();
      val = val.charAt(0).toUpperCase() + val.slice(1);
      answers[idx] = { type: 'text-input', block_id: block.dataset.blockId, value: val };
      personalization[pkey] = val;
      markStarted();
      persist();
      log('Text input', { key: pkey, value: val });
    }

    /* ======== Dual select (sex + neuter) ======== */

    function bindDualSelect(block, idx) {
      var wrap = block.querySelector('.qpgsc-dual');
      var showNeuter = !!(wrap && wrap.dataset.showNeuter === 'true');
      var groupA = Array.prototype.slice.call(block.querySelectorAll('[data-dual-group="a"]'));
      var groupB = Array.prototype.slice.call(block.querySelectorAll('[data-dual-group="b"]'));
      var groupBWrap = block.querySelector('[data-dual-group-b]');
      var groupAInputs = Array.prototype.slice.call(block.querySelectorAll('[data-dual-group="a"] .qpgsc-answer__input'));
      var groupBInputs = Array.prototype.slice.call(block.querySelectorAll('[data-dual-group="b"] .qpgsc-answer__input'));
      var nextBtn = block.querySelector('.qpgsc-next');

      function commit() {
        answers[idx] = {
          type: 'dual-select',
          block_id: block.dataset.blockId,
          sex: personalization.sex,
          pronoun: personalization.pronoun,
          neuter: personalization.neuter
        };
        markStarted();
        persist();
      }

      function isComplete() {
        if (!personalization.sex) return false;
        return showNeuter ? !!personalization.neuter : true;
      }

      function settle() {
        if (!isComplete()) return;
        commit();
        if (nextBtn) nextBtn.disabled = false;
        if (!autoAdvanceRadio) return;
        setTimeout(function () { advanceFrom(idx); }, parseInt(getCssVar('--auto-advance-delay'), 10) || 360);
      }

      groupA.forEach(function (label) {
        var input = label.querySelector('.qpgsc-answer__input');
        label.addEventListener('click', function (e) {
          e.preventDefault();
          groupAInputs.forEach(function (inp) { inp.checked = false; });
          if (input) input.checked = true;
          groupA.forEach(function (l) { l.classList.remove('qpgsc-arow--selected'); });
          label.classList.add('qpgsc-arow--selected');
          personalization.sex = label.dataset.dualValue;
          personalization.pronoun = label.dataset.dualPronoun || 'he';
          if (showNeuter && groupBWrap) {
            groupBWrap.style.display = '';
            commit();
            return;
          }
          settle();
        });
      });

      groupB.forEach(function (label) {
        var input = label.querySelector('.qpgsc-answer__input');
        label.addEventListener('click', function (e) {
          e.preventDefault();
          groupBInputs.forEach(function (inp) { inp.checked = false; });
          if (input) input.checked = true;
          groupB.forEach(function (l) { l.classList.remove('qpgsc-arow--selected'); });
          label.classList.add('qpgsc-arow--selected');
          personalization.neuter = label.dataset.dualValue;
          settle();
        });
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (!isComplete()) return;
          commit();
          advanceFrom(idx);
        });
      }
    }

    /* ======== Interstitial ======== */

    function bindInterstitial(block, idx) {
      var nextBtn = block.querySelector('.qpgsc-next');

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          answers[idx] = { type: 'interstitial', block_id: block.dataset.blockId, viewed: true };
          persist();
          advanceFrom(idx);
        });
      }
    }

    /* ======== Affirmation (auto-advancing) ======== */

    function bindAffirmation(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var inner = block.querySelector('[data-auto-advance-ms]');
        var duration = parseInt(inner ? inner.dataset.autoAdvanceMs : '', 10) || 1200;
        setTimeout(function () { advanceFrom(idx); }, duration);
      }, { once: true });
    }

    /* ======== Loading screen ======== */

    function bindLoading(block) {
      block.addEventListener('quiz:block-shown', function () {
        var loadingEl = block.querySelector('[data-loading]') || block;
        var totalDuration = parseInt(loadingEl.dataset.totalDuration, 10) || 2500;
        var states = Array.prototype.slice.call(block.querySelectorAll('.qpgsc-loading__state'));
        var stateCount = states.length || 1;
        var stateDuration = totalDuration / stateCount;
        var currentState = 0;

        var interval = setInterval(function () {
          currentState++;
          if (currentState >= stateCount) {
            clearInterval(interval);
            return;
          }
          states.forEach(function (s, i) {
            s.classList.toggle('qpgsc-loading__state--active', i === currentState);
          });
        }, stateDuration);

        setTimeout(function () {
          clearInterval(interval);
          completeAndRedirect();
        }, totalDuration);
      }, { once: true });
    }

    /* ======== Navigation ======== */

    function advanceFrom(idx) {
      var nextIdx = idx + 1;
      if (nextIdx >= blocks.length) {
        completeAndRedirect();
        return;
      }
      goToBlock(nextIdx);
    }

    function goToBlock(idx, isInitial) {
      if (idx < 0 || idx >= blocks.length) return;
      var prevBlock = blocks[currentIdx];
      var nextBlock = blocks[idx];

      if (currentIdx >= 0 && history[history.length - 1] !== currentIdx) {
        history.push(currentIdx);
      }

      if (prevBlock && prevBlock !== nextBlock) {
        prevBlock.classList.remove('quiz-pgsc-block--active');
        prevBlock.classList.add('quiz-pgsc-block--exiting');
        setTimeout(function () { prevBlock.classList.remove('quiz-pgsc-block--exiting'); }, 400);
      }

      currentIdx = idx;
      nextBlock.classList.add('quiz-pgsc-block--active');
      if (!isInitial) {
        nextBlock.classList.add('quiz-pgsc-block--entering');
        setTimeout(function () { nextBlock.classList.remove('quiz-pgsc-block--entering'); }, 400);
      }

      applyPersonalization(nextBlock);
      nextBlock.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: idx } }));

      updateChrome();
      persist();
      trackProgress(nextBlock);

      if (!isInitial) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    function goBack() {
      if (history.length === 0) return;
      var prevIdx = history.pop();
      // Skip back over affirmations, they aren't interactive.
      while (prevIdx >= 0 && blocks[prevIdx].dataset.blockType === 'affirmation' && history.length > 0) {
        prevIdx = history.pop();
      }
      if (prevIdx < 0) return;

      var currentBlock = blocks[currentIdx];
      var targetBlock = blocks[prevIdx];
      if (currentBlock) currentBlock.classList.remove('quiz-pgsc-block--active');
      currentIdx = prevIdx;
      targetBlock.classList.add('quiz-pgsc-block--active');
      applyPersonalization(targetBlock);
      updateChrome();
      persist();
    }

    function updateChrome() {
      if (!headerEl) return;

      var currentBlock = blocks[currentIdx];
      var isHero = !!(currentBlock && currentBlock.dataset.hero === 'true');
      var isLoading = !!(currentBlock && currentBlock.dataset.blockType === 'loading');
      var isAffirmation = !!(currentBlock && currentBlock.dataset.blockType === 'affirmation');

      if (isHero) {
        headerEl.style.display = 'none';
        return;
      }

      headerEl.style.display = '';

      if (backBtn) {
        var canGoBack = history.length > 0 && !isLoading && !isAffirmation;
        backBtn.style.display = canGoBack ? '' : 'none';
      }

      // Progress counts answer screens only. The interstitial holds the progress
      // reached by the question before it rather than adding a numbered step.
      var realBlocks = blocks.filter(function (b) { return REAL_STEP_TYPES.indexOf(b.dataset.blockType) !== -1; });
      var currentRealIdx = realBlocks.indexOf(currentBlock);
      if (currentBlock && currentBlock.dataset.blockType === 'interstitial') {
        currentRealIdx = realBlocks.filter(function (b) { return blocks.indexOf(b) < currentIdx; }).length - 1;
      }

      if (stepLabel) {
        stepLabel.textContent = currentRealIdx >= 0 ? (currentRealIdx + 1) + ' / ' + realBlocks.length : '';
      }

      if (progressFill && realBlocks.length > 0) {
        var pct = currentRealIdx >= 0 ? ((currentRealIdx + 1) / realBlocks.length) * 100 : 0;
        progressFill.style.width = pct + '%';
      }
    }

    function applyPersonalization(block) {
      Array.prototype.forEach.call(block.querySelectorAll('[data-personalize]'), function (el) {
        var template = el.getAttribute('data-personalize');
        if (!template) return;
        el.innerHTML = renderTemplate(template);
      });

      // Interstitial body copy holds rich HTML, so it can't live in an attribute.
      // Tokenised in place; idempotent once the tokens are replaced.
      Array.prototype.forEach.call(block.querySelectorAll('.qpgsc-interstitial__body'), function (el) {
        if (el.innerHTML.indexOf('{') === -1) return;
        el.innerHTML = renderTemplate(el.innerHTML);
      });
    }

    function renderTemplate(str) {
      if (!str) return '';
      return String(str).replace(/\{(\w+)\}/g, function (match, key) {
        return personalization[key] !== undefined && personalization[key] !== ''
          ? escapeHtml(String(personalization[key]))
          : match;
      });
    }

    /* ======== Copy split testing ======== */

    function initializeCopyTest() {
      var enabled = root.dataset.copyTestEnabled === 'true';
      var name = (root.dataset.copyTestName || 'itch-copy-test-01').trim();
      var bPercent = Math.max(0, Math.min(100, parseInt(root.dataset.copyTestBPercent, 10) || 0));
      var configEl = root.querySelector('[data-copy-test-variant-b]');
      var variantB = {};

      if (configEl && configEl.textContent.trim()) {
        try {
          variantB = JSON.parse(configEl.textContent);
        } catch (error) {
          log('Variant B copy JSON is invalid; serving variant A.', error);
          variantB = {};
        }
      }

      var hasVariantB = variantB && Object.keys(variantB).length > 0;
      var forcedVariant = new URLSearchParams(window.location.search).get('quiz_variant');
      forcedVariant = forcedVariant === 'a' || forcedVariant === 'b' ? forcedVariant : '';
      var variant = 'a';
      var assignmentKey = storageKey + ':copy-test:' + name;

      if (forcedVariant) {
        variant = forcedVariant;
      } else if (enabled && hasVariantB) {
        try {
          var storedVariant = localStorage.getItem(assignmentKey);
          if (storedVariant === 'a' || storedVariant === 'b') {
            variant = storedVariant;
          } else {
            variant = Math.random() * 100 < bPercent ? 'b' : 'a';
            localStorage.setItem(assignmentKey, variant);
          }
        } catch (error) {
          variant = Math.random() * 100 < bPercent ? 'b' : 'a';
        }
      }

      if (variant === 'b' && hasVariantB) {
        applyCopyVariant(variantB);
      } else if (!hasVariantB) {
        variant = 'a';
      }

      root.dataset.copyVariant = variant;
      log('Copy test assignment', { name: name, variant: variant, enabled: enabled });
      return { name: name, variant: variant };
    }

    function applyCopyVariant(config) {
      var hero = config.hero || {};
      if (hero.headline !== undefined) {
        var headlineEl = root.querySelector('.qpgsc-hero__headline');
        if (headlineEl) headlineEl.innerHTML = hero.headline;
      }
      setText(root.querySelector('.qpgsc-hero__intro-text'), hero.intro);
      setText(root.querySelector('.qpgsc-hero__quote'), hero.proof_quote);

      var blockCopy = config.blocks || {};
      blocks.forEach(function (block) {
        var copy = blockCopy[block.dataset.blockId];
        if (!copy) return;

        setPersonalizedText(block.querySelector('.qpgsc-question__title'), copy.title);
        setText(block.querySelector('.qpgsc-question__hint'), copy.helper_text);
        setText(block.querySelector('.qpgsc-next'), copy.button_text);

        var answerCopy = copy.answers || {};
        Array.prototype.forEach.call(block.querySelectorAll('[data-answer-value]'), function (answer) {
          var replacement = answerCopy[answer.dataset.answerValue];
          if (replacement === undefined) return;
          setPersonalizedText(answer.querySelector('.qpgsc-arow__label'), replacement);
          answer.dataset.answerText = replacement;
        });
      });
    }

    function setText(element, value) {
      if (element && value !== undefined && value !== null) {
        element.textContent = value;
      }
    }

    function setPersonalizedText(element, value) {
      setText(element, value);
      if (element && value !== undefined && value !== null && element.hasAttribute('data-personalize')) {
        element.setAttribute('data-personalize', value);
      }
    }

    function pushQuizEvent(eventName, extra) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({
        event: eventName,
        quiz_id: sessionId,
        copy_test_name: personalization.copy_test_name || '',
        copy_variant: personalization.copy_variant || 'a'
      }, extra || {}));
    }

    function markStarted() {
      if (hasStarted) return;
      hasStarted = true;
      pushQuizEvent('quiz_started');
    }

    /* ======== Persistence ======== */

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          sessionId: sessionId,
          startedAt: startedAt,
          currentIdx: currentIdx,
          history: history,
          answers: answers,
          personalization: personalization,
          hasStarted: hasStarted
        }));
      } catch (e) {
        log('Persist failed', e);
      }
    }

    function rehydrate() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var state = JSON.parse(raw);
        if (!state || !state.sessionId) return;
        // Only rehydrate if started within the last 2 hours.
        if (Date.now() - state.startedAt > 7200000) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        sessionId = state.sessionId;
        startedAt = state.startedAt;
        currentIdx = typeof state.currentIdx === 'number' ? state.currentIdx : -1;
        history = state.history || [];
        answers = state.answers || {};
        hasStarted = !!state.hasStarted;
        personalization = Object.assign(personalization, state.personalization || {});
        log('Rehydrated', { currentIdx: currentIdx });

        if (currentIdx >= 0 && blocks[currentIdx]) {
          blocks.forEach(function (b) { b.classList.remove('quiz-pgsc-block--active'); });
          blocks[currentIdx].classList.add('quiz-pgsc-block--active');
          applyPersonalization(blocks[currentIdx]);
        } else {
          currentIdx = -1;
        }
      } catch (e) {
        log('Rehydrate failed', e);
      }
    }

    /* ======== Completion ======== */

    function completeAndRedirect() {
      var url = buildOfferUrl();
      log('Redirecting to', url);

      pushQuizEvent('quiz_completed');

      try {
        var trackableBlocks = blocks.filter(function (b) {
          return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
        });
        sendTracking({
          completed: true,
          stepId: 'completed',
          stepIndex: trackableBlocks.length,
          totalSteps: trackableBlocks.length
        });
      } catch (e) { log('sendTracking (complete) failed', e); }

      try { writeCartAttribute(); } catch (e) { log('writeCartAttribute failed', e); }
      try { sessionStorage.removeItem(storageKey); } catch (e) {}

      setTimeout(function () { window.location.href = url; }, 50);
    }

    /**
     * The offer URL is used as configured. Nothing from the quiz is appended:
     * answers travel as cart attributes. UTM parameters are passed through only
     * when the section setting is switched on.
     */
    function buildOfferUrl() {
      var base = offerUrl || '/';
      if (!forwardUtm) return base;

      var incoming = new URLSearchParams(window.location.search);
      var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      var url;
      var isRelative = base.charAt(0) === '/' && base.charAt(1) !== '/';

      try {
        url = new URL(base, window.location.origin);
      } catch (e) {
        return base;
      }

      keys.forEach(function (k) {
        var v = incoming.get(k);
        if (v) url.searchParams.set(k, v);
      });

      return isRelative ? url.pathname + url.search + url.hash : url.toString();
    }

    /**
     * POST the current quiz state to the Apps Script endpoint. Called on every
     * step (drop-off tracking) and again at completion. The endpoint upserts by
     * quiz_id, so repeated calls update one row holding the furthest step.
     *
     * The payload keeps its original key set so existing Sheet columns stay put.
     * Keys for removed questions (result_type, breed, q8_worst, q9_*) are sent
     * empty rather than dropped.
     */
    function sendTracking(opts) {
      opts = opts || {};
      var endpoint = root.dataset.trackingEndpoint || '';
      if (!endpoint) { log('No tracking endpoint set, skipping sendTracking'); return; }

      var incoming = new URLSearchParams(window.location.search);
      var payload = {
        quiz_id: sessionId,
        started_at: new Date(startedAt).toISOString(),
        completed: !!opts.completed,
        last_step_id: opts.stepId || '',
        step_index: (opts.stepIndex != null ? opts.stepIndex : ''),
        total_steps: (opts.totalSteps != null ? opts.totalSteps : ''),
        submitted_at: opts.completed ? new Date().toISOString() : '',
        result_type: '',
        copy_test_name: personalization.copy_test_name || '',
        copy_variant: personalization.copy_variant || 'a',
        dog_name: personalization.dog_name || '',
        age: personalization.age || '',
        breed: '',
        weight_bracket: personalization.weight_bracket || '',
        sex: personalization.sex || '',
        neuter: personalization.neuter || '',
        q5_symptoms_top_2: personalization.q5_symptoms_top_2 || '',
        q6_pattern: personalization.q6_pattern || '',
        q7_count: personalization.q7_count_numeric || 0,
        q8_worst: '',
        q9_number: '',
        q9_anchor: '',
        q10_medication: personalization.q10_medication || '',
        tried_category: personalization.tried_category || '',
        value_anchor: personalization.value_anchor || '',
        drug_interaction_flag: !!personalization.drug_interaction_flag,
        page_url: window.location.href,
        utm_source: incoming.get('utm_source') || '',
        utm_medium: incoming.get('utm_medium') || '',
        utm_campaign: incoming.get('utm_campaign') || ''
      };

      var body = JSON.stringify(payload);
      var sent = false;

      // sendBeacon survives navigation. text/plain avoids a CORS preflight that
      // Apps Script doesn't answer, while still POSTing the JSON body.
      if (navigator.sendBeacon) {
        try {
          sent = navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        } catch (e) { sent = false; }
      }
      if (!sent) {
        try {
          fetch(endpoint, {
            method: 'POST',
            mode: 'no-cors',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: body
          });
        } catch (e) { log('fetch tracking fallback failed', e); }
      }
      log('Tracking sent', { quiz_id: payload.quiz_id, step: payload.last_step_id, completed: payload.completed });
    }

    function trackProgress(block) {
      if (!block) return;
      if (TRACKABLE_STEP_TYPES.indexOf(block.dataset.blockType) === -1) return;

      var trackableBlocks = blocks.filter(function (b) {
        return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
      });

      try {
        sendTracking({
          completed: false,
          stepId: block.dataset.blockId || block.dataset.blockType,
          stepIndex: trackableBlocks.indexOf(block) + 1,
          totalSteps: trackableBlocks.length
        });
      } catch (e) { log('trackProgress failed', e); }
    }

    /**
     * Stamp the Shopify cart with the quiz answers. These ride to the order, and
     * Klaviyo ingests Shopify orders automatically, so every field here lands on
     * the Placed Order event with no email gate and no API keys. This is now the
     * ONLY channel carrying quiz answers to the offer page's eventual order.
     */
    function writeCartAttribute() {
      var attrs = {
        quiz_id: sessionId,
        quiz_copy_test: personalization.copy_test_name || '',
        quiz_copy_variant: personalization.copy_variant || 'a',
        quiz_dog_name: personalization.dog_name || '',
        quiz_age: personalization.age_label || personalization.age || '',
        quiz_weight: personalization.weight_bracket || '',
        quiz_sex: personalization.sex || '',
        quiz_symptoms: personalization.q5_symptoms_top_2 || '',
        quiz_pattern: personalization.q6_pattern || '',
        quiz_tried_count: personalization.q7_count_numeric || 0,
        quiz_tried_before: personalization.previous_brand || '',
        quiz_tried_category: personalization.tried_category || '',
        quiz_motivation: personalization.value_anchor || '',
        quiz_on_vet_meds: personalization.drug_interaction_flag ? 'yes' : 'no'
      };

      var attributes = {};
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] !== '' && attrs[k] !== null && attrs[k] !== undefined) {
          attributes[k] = attrs[k];
        }
      });

      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        });
        log('Cart attributes written', attributes);
      } catch (e) {
        log('cart attribute write failed', e);
      }
    }

    /* ======== Utilities ======== */

    function generateSessionId() {
      return 'pgsc_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    }

    function getCssVar(name) {
      var el = document.querySelector('.quiz-pgsc');
      if (!el) return '';
      return getComputedStyle(el).getPropertyValue(name).trim();
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();